<script setup lang="ts">
import TotalCard from "./components/totalcard.vue";
import VillageCard from "./components/villagecard.vue";
import EditRequestDialog from "./components/EditRequestDialog.vue";
import {
  UsersIcon,
  BuildingIcon,
  UsersGroupIcon,
  BriefcaseIcon,
  MapPinIcon,
  CalendarIcon,
  PhoneIcon,
  SchoolIcon,
  HeartIcon,
  CheckIcon,
  EditIcon,
  CrownIcon,
  ChessQueenFilledIcon,
  BuildingSkyscraperIcon,
  SearchIcon,
  ArrowLeftIcon,
  CopyIcon,
  SparklesIcon,
  HomeIcon,
  LockIcon,
} from "vue-tabler-icons";
import { ref, computed, watch, nextTick } from "vue";
import { useVillagesStore, type Member } from '@/stores/villages';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';

const villagesStore = useVillagesStore();

// Ensure villages data is loaded
if (villagesStore.villages.length === 0) {
  villagesStore.fetchVillagesFromSheet();
}

const { searchQuery, searchResults, homeSignal } = storeToRefs(villagesStore);
const hasSearch = computed(() => searchQuery.value.trim().length > 0);
const route = useRoute();
const router = useRouter();

const selectedVillage = ref<any | null>(null);
const selectedFamily = ref<any | null>(null);

// State for copying phone number
const copiedPhone = ref<string | null>(null);

const copyPhoneNumber = (phone: string) => {
  navigator.clipboard.writeText(phone);
  copiedPhone.value = phone;
  setTimeout(() => {
    copiedPhone.value = null;
  }, 2000);
};

// State for Edit Request Dialog
const showEditDialog = ref(false);
const selectedMemberForEdit = ref<Member | null>(null);

const openEditRequest = (mem: Member) => {
  selectedMemberForEdit.value = mem;
  showEditDialog.value = true;
};

const selectVillage = (v: any) => {
  selectedVillage.value = v;
  if (v?.id) router.push({ name: 'Village', params: { id: v.id } });
};

const goBack = () => {
  selectedVillage.value = null;
  router.push({ name: 'Home' });
};

const getHeadName = (fam: any) => {
  if (!fam || !fam.members || !fam.members.length) return "-";
  const head = fam.members.find((m: any) => ['head', 'self', 'મુખિયા', 'મુખીયા'].includes((m.role || "").toString().toLowerCase()));
  return head ? head.name : fam.members[0].name;
};

const roleLabel = (role: string | undefined | null) => {
  const r = (role || "").toString().trim().toLowerCase();
  if (r === "head" || r === "self") return "મુખિયા";
  if (r === "wife") return "પત્ની";
  if (r === "son") return "દીકરો";
  if (r === "daughter") return "દીકરી";
  if (r === "mother") return "માતા";
  if (r === "father") return "પિતા";
  return role || "સભ્ય";
};

const selectFamily = (f: any) => {
  selectedFamily.value = f;
  if (selectedVillage.value?.id && f?.id) {
    router.push({ name: 'Family', params: { id: selectedVillage.value.id, familyId: f.id } });
  }
};

const backToVillage = () => {
  selectedFamily.value = null;
  if (selectedVillage.value?.id) {
    router.push({ name: 'Village', params: { id: selectedVillage.value.id } });
  }
};

const suppressSearchWatch = ref(false);
const normalize = (v: any) => (v || "").toString().trim().toLowerCase();

const openFromSearch = async (res: any) => {
  suppressSearchWatch.value = true;
  const village =
    (res.villageId && villagesStore.villages.find((v: any) => v.id === res.villageId)) ||
    villagesStore.villages.find((v: any) => normalize(v.villageName) === normalize(res.villageName));
  if (!village) {
    suppressSearchWatch.value = false;
    return;
  }
  const family =
    (res.familyId && village.families?.find((f: any) => f.id === res.familyId)) ||
    (res.familyName && village.families?.find((f: any) => normalize(f.familyName) === normalize(res.familyName))) ||
    null;
  selectedVillage.value = village;
  selectedFamily.value = family;
  if (family?.id) {
    router.push({ name: 'Family', params: { id: village.id, familyId: family.id } });
  } else {
    router.push({ name: 'Village', params: { id: village.id } });
  }
  villagesStore.clearSearch();
  await nextTick();
  suppressSearchWatch.value = false;
};

watch(searchQuery, () => {
  if (suppressSearchWatch.value) return;
  selectedVillage.value = null;
  selectedFamily.value = null;
});

watch(homeSignal, () => {
  villagesStore.clearSearch();
  selectedVillage.value = null;
  selectedFamily.value = null;
  router.push({ name: 'Home' });
});

watch(
  [() => route.params.id, () => route.params.familyId, () => villagesStore.villages],
  () => {
    const id = route.params.id as string | undefined;
    const familyId = route.params.familyId as string | undefined;
    if (!id) {
      selectedVillage.value = null;
      selectedFamily.value = null;
      return;
    }
    const village = villagesStore.villages.find((v: any) => v.id === id);
    if (!village) {
      selectedVillage.value = null;
      selectedFamily.value = null;
      return;
    }
    selectedVillage.value = village;
    if (familyId) {
      const family = village.families?.find((f: any) => f.id === familyId) || null;
      selectedFamily.value = family;
    } else {
      selectedFamily.value = null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="directory-container">
    
    <!-- -------------------------------------------------------------------- -->
    <!-- 1. ROOT VIEW: HERO, METRICS & VILLAGES GRID -->
    <!-- -------------------------------------------------------------------- -->
    <template v-if="!selectedVillage">
      
      <!-- SEARCH RESULTS VIEW -->
      <template v-if="hasSearch">
        <div class="d-flex align-center justify-space-between mb-3 flex-wrap" style="gap: 10px;">
          <div>
            <div class="text-h6 font-weight-black">
              શોધ પરિણામો: "{{ searchQuery }}"
            </div>
            <div class="text-caption text-medium-emphasis">
              કુલ મેળ ખાતા સભ્યો: {{ searchResults.length }}
            </div>
          </div>

          <v-btn color="secondary" variant="tonal" size="small" rounded="lg" @click="villagesStore.clearSearch()">
            શોધ સાફ કરો
          </v-btn>
        </div>

        <v-row v-if="searchResults.length" dense>
          <v-col
            v-for="(res, idx) in searchResults"
            :key="res.member?.name + idx"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card elevation="0" class="search-result-card depth-card pa-3 pa-sm-4" @click="openFromSearch(res)">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-subtitle-2 font-weight-bold text-primary">{{ res.member?.name }}</div>
                  <div class="text-caption text-medium-emphasis">
                    ગામ: <strong>{{ res.villageName }}</strong> | મુખિયા: {{ res.headName }}
                  </div>
                </div>

                <v-chip size="x-small" variant="tonal" color="secondary" class="font-weight-bold">
                  {{ res.member?.role ? roleLabel(res.member.role) : 'સભ્ય' }}
                </v-chip>
              </div>

              <div class="d-flex align-center justify-space-between mt-2 pt-2 border-t text-caption text-medium-emphasis">
                <span class="text-truncate" style="max-width: 160px;">{{ res.member?.occupation || '-' }}</span>
                <span class="text-primary font-weight-bold">પરિવાર જુઓ &rarr;</span>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col cols="12">
            <v-alert
              v-if="searchQuery.trim().toLowerCase().includes('admin')"
              type="info"
              variant="tonal"
              class="rounded-xl"
            >
              <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <LockIcon size="18" class="text-primary mr-1" />
                <span>
                  <strong>એડમિન મોડ શોધાયો:</strong> લૉગિન કરવા માટે ઉપર હેડરમાં <strong>"એડમિન લૉગિન"</strong> બટન પર ક્લિક કરો.
                </span>
              </div>
            </v-alert>
            <v-alert v-else type="info" variant="tonal" class="rounded-xl">
              આ શોધ માટે કોઈ સભ્યો મળ્યા નથી. કૃપા કરીને બીજું નામ અથવા ગામ લખી પ્રયાસ કરો.
            </v-alert>
          </v-col>
        </v-row>
      </template>

      <!-- NORMAL DIRECTORY DASHBOARD -->
      <template v-else>
        <!-- Top Hero Banner -->
        <v-card elevation="0" class="hero-banner pa-4 pa-sm-6 rounded-2xl mb-4 position-relative overflow-hidden">
          <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
            <div>
              <div class="d-flex align-center mb-1 flex-wrap" style="gap: 6px;">
                <v-chip size="x-small" color="primary" variant="flat" class="font-weight-bold text-uppercase">
                  ડિરેક્ટરી
                </v-chip>
                <span class="text-caption text-medium-emphasis font-weight-bold">કડવા પાટીદાર • જય અંબે</span>
              </div>
              <h1 class="hero-title font-weight-black mb-1">
                ભોજાણી પરિવાર ડિરેક્ટરી
              </h1>
              <p class="text-caption text-sm-subtitle-2 text-medium-emphasis mb-0">
                સમગ્ર ભોજાણી પરિવારના ગામો, કુટુંબો અને સભ્યોની વિગતો
              </p>
            </div>

            <!-- Quick Stats Badge -->
            <div class="d-flex align-center flex-wrap" style="gap: 6px;">
              <v-chip color="primary" variant="tonal" size="small" class="font-weight-bold">
                <BuildingIcon size="15" class="mr-1" /> {{ villagesStore.totalVillages }} ગામ
              </v-chip>
              <v-chip color="secondary" variant="tonal" size="small" class="font-weight-bold">
                <UsersGroupIcon size="15" class="mr-1" /> {{ villagesStore.totalFamilies }} કુટુંબ
              </v-chip>
            </div>
          </div>
        </v-card>

        <!-- Metric Summary Cards (4-Column Grid, Responsive 2x2 on Mobile) -->
        <v-row align="stretch" class="mb-4" dense>
          <v-col cols="6" sm="6" md="3" class="d-flex">
            <TotalCard
              label="કુલ ગામ"
              sublabel="Villages"
              :count="villagesStore.totalVillages"
              :icon="BuildingIcon"
              color="primary"
              gradient="linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)"
            />
          </v-col>

          <v-col cols="6" sm="6" md="3" class="d-flex">
            <TotalCard
              label="કુલ કુટુંબ"
              sublabel="Families"
              :count="villagesStore.totalFamilies"
              :icon="UsersGroupIcon"
              color="secondary"
              gradient="linear-gradient(135deg, #7b1fa2 0%, #5e35b1 100%)"
            />
          </v-col>

          <v-col cols="6" sm="6" md="3" class="d-flex">
            <TotalCard
              label="કુલ વ્યક્તિ"
              sublabel="Members"
              :count="villagesStore.totalPeople"
              :icon="UsersIcon"
              color="info"
              gradient="linear-gradient(135deg, #0288d1 0%, #0097a7 100%)"
            />
          </v-col>

          <v-col cols="6" sm="6" md="3" class="d-flex">
            <TotalCard
              label="મુખિયાઓ"
              sublabel="Heads"
              :count="villagesStore.totalFamilies"
              :icon="CrownIcon"
              color="warning"
              gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            />
          </v-col>
        </v-row>

        <!-- Villages Grid Header -->
        <div class="d-flex align-center justify-space-between mb-3">
          <div>
            <h2 class="text-h6 font-weight-black section-heading">ગામ પ્રમાણે ડિરેક્ટરી</h2>
            <div class="text-caption text-medium-emphasis">કોઈપણ ગામ પર ક્લિક કરી કુટુંબો જુઓ</div>
          </div>
          <v-chip color="primary" variant="outlined" size="x-small" class="font-weight-bold">
            {{ villagesStore.villages.length }} ગામ
          </v-chip>
        </div>

        <!-- Render All Villages -->
        <v-row dense>
          <v-col
            v-for="(village, idx) in villagesStore.villages"
            :key="village.id || idx"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="d-flex"
          >
            <VillageCard
              :label="village.villageName"
              :count="village.families ? village.families.length : 0"
              :village="village"
              @select="selectVillage"
            />
          </v-col>
        </v-row>
      </template>
    </template>

    <!-- -------------------------------------------------------------------- -->
    <!-- 2. VILLAGE / FAMILY VIEW -->
    <!-- -------------------------------------------------------------------- -->
    <template v-else>
      
      <!-- BREADCRUMB & SECTION HEADER -->
      <v-card elevation="0" class="breadcrumb-header pa-3 pa-sm-4 rounded-xl mb-3 depth-card">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
          <!-- Breadcrumb Path -->
          <div class="d-flex align-center flex-wrap" style="gap: 6px;">
            <v-btn icon size="x-small" color="primary" variant="tonal" title="મુખ્ય પાનું" @click="goBack">
              <HomeIcon size="16" />
            </v-btn>
            <span class="text-medium-emphasis">/</span>

            <!-- Village Link -->
            <v-btn
              variant="text"
              color="primary"
              size="small"
              class="font-weight-bold px-1 px-sm-2"
              @click="selectedFamily ? backToVillage() : null"
            >
              <BuildingIcon size="15" class="mr-1" /> {{ selectedVillage.villageName }}
            </v-btn>

            <!-- Family Part (if inside family) -->
            <template v-if="selectedFamily">
              <span class="text-medium-emphasis">/</span>
              <v-chip color="secondary" variant="flat" size="x-small" class="font-weight-bold">
                {{ getHeadName(selectedFamily) }}
              </v-chip>
            </template>
          </div>

          <!-- Back Navigation Button -->
          <v-btn
            v-if="selectedFamily"
            color="secondary"
            variant="tonal"
            size="small"
            rounded="lg"
            class="font-weight-bold"
            @click="backToVillage"
          >
            <ArrowLeftIcon size="15" class="mr-1" /> <span class="d-none d-sm-inline">ગામના અન્ય કુટુંબ</span><span class="d-inline d-sm-none">પાછા</span>
          </v-btn>
          <v-btn
            v-else
            color="secondary"
            variant="tonal"
            size="small"
            rounded="lg"
            class="font-weight-bold"
            @click="goBack"
          >
            <ArrowLeftIcon size="15" class="mr-1" /> <span class="d-none d-sm-inline">બધા ગામ</span><span class="d-inline d-sm-none">પાછા</span>
          </v-btn>
        </div>
      </v-card>

      <!-- 2.A: FAMILIES IN VILLAGE VIEW -->
      <template v-if="!selectedFamily">
        <div class="d-flex align-center justify-space-between mb-3">
          <div>
            <h2 class="text-h6 font-weight-black">
              {{ selectedVillage.villageName }} ગામના કુટુંબો
            </h2>
            <div class="text-caption text-medium-emphasis">
              કુલ {{ selectedVillage.families ? selectedVillage.families.length : 0 }} કુટુંબ
            </div>
          </div>
        </div>

        <v-row dense>
          <v-col
            v-for="(fam, i) in selectedVillage.families"
            :key="fam.id || i"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              elevation="0"
              class="family-card depth-card pa-3 pa-sm-4"
              style="cursor: pointer"
              @click="selectFamily(fam)"
            >
              <div class="d-flex align-start justify-space-between">
                <div class="d-flex align-center" style="gap: 10px;">
                  <v-avatar size="38" color="secondary" class="avatar-3d flex-shrink-0">
                    <CrownIcon size="20" stroke-width="1.8" class="text-white" />
                  </v-avatar>
                  <div class="min-width-0">
                    <h3 class="text-subtitle-2 font-weight-black mb-0 text-truncate">{{ getHeadName(fam) }}</h3>
                    <div class="text-caption text-medium-emphasis">
                      ID: {{ fam.id }}
                    </div>
                  </div>
                </div>

                <v-chip color="primary" variant="tonal" size="x-small" class="font-weight-bold">
                  {{ fam.members ? fam.members.length : 0 }} સભ્યો
                </v-chip>
              </div>

              <div class="d-flex align-center justify-space-between mt-2 pt-2 border-t text-caption text-medium-emphasis">
                <span class="text-truncate" style="max-width: 180px;">
                  {{ fam.members?.[0]?.currentResidence || selectedVillage.villageName }}
                </span>
                <span class="text-primary font-weight-bold">જુઓ &rarr;</span>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- 2.B: MEMBERS IN FAMILY VIEW -->
      <template v-else>
        <!-- Family Summary Top Card -->
        <v-card elevation="0" class="family-detail-hero pa-3 pa-sm-4 rounded-xl mb-3 depth-card">
          <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 10px;">
            <div class="d-flex align-center" style="gap: 10px;">
              <v-avatar size="42" color="primary" class="avatar-3d flex-shrink-0">
                <CrownIcon size="22" class="text-white" />
              </v-avatar>
              <div>
                <h2 class="text-subtitle-1 font-weight-black mb-0">{{ getHeadName(selectedFamily) }}</h2>
                <div class="text-caption text-medium-emphasis">
                  {{ selectedVillage.villageName }} • કુટુંબ ID: {{ selectedFamily.id }}
                </div>
              </div>
            </div>

            <v-chip color="primary" variant="flat" size="small" class="font-weight-bold">
              <UsersIcon size="15" class="mr-1" /> {{ selectedFamily.members ? selectedFamily.members.length : 0 }} સભ્યો
            </v-chip>
          </div>
        </v-card>

        <!-- Member Cards Grid -->
        <v-row dense>
          <v-col
            v-for="(mem, j) in selectedFamily.members"
            :key="mem.id || j"
            cols="12"
            md="6"
          >
            <v-card elevation="0" class="member-profile-card depth-card pa-3 pa-sm-4 position-relative">
              <div class="d-flex align-start">
                <!-- Avatar by Role / Gender -->
                <v-avatar
                  size="42"
                  class="mr-2 mr-sm-3 avatar-3d flex-shrink-0"
                  :color="mem.sex === 'Female' ? 'secondary' : (['head', 'self'].includes((mem.role || '').toLowerCase()) ? 'warning' : 'primary')"
                >
                  <ChessQueenFilledIcon v-if="mem.sex === 'Female'" size="22" class="text-white" />
                  <CrownIcon v-else-if="['head', 'self'].includes((mem.role || '').toLowerCase())" size="22" class="text-white" />
                  <UsersIcon v-else size="22" class="text-white" />
                </v-avatar>

                <div class="flex-grow-1 min-width-0">
                  <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 4px;">
                    <div class="min-width-0">
                      <h3 class="text-subtitle-2 font-weight-black member-name mb-0 text-truncate">{{ mem.name }}</h3>
                      <div v-if="mem.nameE" class="text-caption text-medium-emphasis text-truncate">
                        {{ mem.nameE }}
                      </div>
                    </div>

                    <div class="d-flex align-center flex-wrap" style="gap: 4px;">
                      <v-chip size="x-small" variant="flat" :color="['head', 'self'].includes((mem.role || '').toLowerCase()) ? 'warning' : 'primary'" class="font-weight-bold">
                        {{ roleLabel(mem.role) }}
                      </v-chip>

                      <v-chip size="x-small" variant="tonal" color="secondary" class="font-weight-bold">
                        {{ mem.age ? `${mem.age}વર્ષ` : '-' }}
                      </v-chip>

                      <v-btn
                        size="x-small"
                        variant="tonal"
                        color="primary"
                        rounded="sm"
                        class="font-weight-medium px-2"
                        title="વિગતો સુધારવા માટે વિનંતી કરો"
                        @click="openEditRequest(mem)"
                      >
                        <EditIcon size="12" class="mr-1" />
                        <span class="d-none d-sm-inline">સુધારો સૂચવો</span>
                        <span class="d-inline d-sm-none">સુધારો</span>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Member Meta Details Grid -->
                  <div class="meta-grid mt-2 pt-2 border-t">
                    <!-- 1. Contact / Mobile Number -->
                    <div class="meta-item">
                      <PhoneIcon size="14" class="text-primary mr-1 flex-shrink-0" />
                      <span class="meta-label">મોબાઇલ:</span>
                      <span class="meta-val font-weight-bold">
                        <template v-if="mem.contact">
                          <a :href="`tel:${mem.contact}`" class="text-primary text-decoration-none font-weight-black">
                            {{ mem.contact }}
                          </a>
                          <v-btn
                            icon
                            size="x-small"
                            variant="text"
                            color="primary"
                            class="ml-1"
                            title="કોપી કરો"
                            @click="copyPhoneNumber(mem.contact)"
                          >
                            <CheckIcon v-if="copiedPhone === mem.contact" size="12" class="text-success" />
                            <CopyIcon v-else size="12" />
                          </v-btn>
                        </template>
                        <template v-else>
                          <span class="text-medium-emphasis">-</span>
                        </template>
                      </span>
                    </div>

                    <!-- 2. Occupation -->
                    <div v-if="mem.occupation" class="meta-item">
                      <BriefcaseIcon size="14" class="text-secondary mr-1 flex-shrink-0" />
                      <span class="meta-label">વ્યવસાય:</span>
                      <span class="meta-val text-truncate">{{ mem.occupation }}</span>
                    </div>

                    <!-- 3. Current Residence -->
                    <div v-if="mem.currentResidence" class="meta-item full-width">
                      <MapPinIcon size="14" class="text-error mr-1 flex-shrink-0" />
                      <span class="meta-label">સરનામું:</span>
                      <span class="meta-val">{{ mem.currentResidence }}</span>
                    </div>

                    <!-- 4. Education -->
                    <div v-if="mem.education" class="meta-item">
                      <SchoolIcon size="14" class="text-info mr-1 flex-shrink-0" />
                      <span class="meta-label">અભ્યાસ:</span>
                      <span class="meta-val text-truncate">{{ mem.education }}</span>
                    </div>

                    <!-- 5. Maternal -->
                    <div v-if="mem.maternal" class="meta-item">
                      <HeartIcon size="14" class="text-pink mr-1 flex-shrink-0" />
                      <span class="meta-label">મોસાળ:</span>
                      <span class="meta-val text-truncate">{{ mem.maternal }}</span>
                    </div>

                    <!-- 6. DOB -->
                    <div v-if="mem.dob" class="meta-item">
                      <CalendarIcon size="14" class="text-success mr-1 flex-shrink-0" />
                      <span class="meta-label">જન્મ તારીખ:</span>
                      <span class="meta-val">{{ mem.dob }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>

    <!-- Edit Request Dialog (Submit for Admin Review) -->
    <EditRequestDialog
      v-model="showEditDialog"
      :member="selectedMemberForEdit"
      :village-id="selectedVillage?.id || ''"
      :village-name="selectedVillage?.villageName || ''"
      :family-id="selectedFamily?.id || ''"
      :head-name="getHeadName(selectedFamily)"
    />
  </div>
</template>

<style scoped>
.directory-container {
  max-width: 1400px;
  margin: 0 auto;
}

.hero-banner {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(241, 245, 249, 0.92) 100%);
  border: 1px solid rgba(226, 232, 240, 0.85);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
}

.hero-title {
  color: #0f172a;
  font-size: 1.5rem;
  letter-spacing: -0.4px;
}

.section-heading {
  color: #0f172a;
}

.breadcrumb-header,
.family-detail-hero {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.family-card {
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(226, 232, 240, 0.9);
  transition: all 0.22s ease;
}

.family-card:hover {
  transform: translateY(-3px);
  border-color: rgba(var(--v-theme-primary), 0.35);
  box-shadow: 0 12px 24px rgba(30, 136, 229, 0.12) !important;
}

.member-profile-card {
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(226, 232, 240, 0.9);
  transition: all 0.22s ease;
}

.member-profile-card:hover {
  transform: translateY(-2px);
  border-color: rgba(var(--v-theme-primary), 0.3);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07) !important;
}

.member-name {
  color: #0f172a;
  line-height: 1.25;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 6px 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  min-width: 0;
}

.meta-item.full-width {
  grid-column: 1 / -1;
}

.meta-label {
  color: #64748b;
  margin-right: 5px;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.meta-val {
  color: #1e293b;
  display: flex;
  align-items: center;
  min-width: 0;
}

.search-result-card {
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(226, 232, 240, 0.9);
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-result-card:hover {
  transform: translateY(-2px);
  border-color: rgba(var(--v-theme-primary), 0.4);
  box-shadow: 0 10px 20px rgba(30, 136, 229, 0.1) !important;
}

@media (min-width: 600px) {
  .hero-title {
    font-size: 1.85rem;
  }
}

@media (max-width: 600px) {
  .meta-grid {
    grid-template-columns: 1fr;
  }
}
</style>
